/**
 * Business Details tab (spec 2026-07-22 §2.2/§2.3; UI pass spec 2026-07-26
 * §1): business name, IANA timezone, contact phone. Deployment-wide
 * singleton; phone/name double as the default Lalamove courier sender
 * identity (PR 2, decision Q5).
 */

import { useEffect, useRef, useState } from 'react'
import {
  Alert, AlertDescription, Button, Card, CardContent, CardDescription,
  CardHeader, CardTitle, Input, Spinner, Textarea, useToast,
} from '@/components/ui'
import { TimezonePicker } from '@/components/admin/TimezonePicker'
import { apiClient } from '@/services/apiClient'
import { useFeatureStore } from '@/stores/featureStore'

export function BusinessDetailsTab() {
  const [isLoading, setIsLoading] = useState(true)
  const [name, setName] = useState('')
  const [timezone, setTimezone] = useState<string | null>(null)
  const [phone, setPhone] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [bankBeneficiary, setBankBeneficiary] = useState('')
  const [bankName, setBankName] = useState('')
  const [bankClabe, setBankClabe] = useState('')
  const [bankInstructions, setBankInstructions] = useState('')
  const [bankImage, setBankImage] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  // SA-243: handoff follow-up knobs. `''` means "not set" — the PUT
  // contract for these two fields is set-only (omit the key = leave the
  // deployment's current value untouched), so an empty input must not
  // coerce to 0 and clobber it. `?? ''` (not `||`) so a loaded 0 (a
  // deliberate "off") renders as 0, not empty.
  const [slaReminderMinutes, setSlaReminderMinutes] = useState<number | ''>('')
  const [reengageMinutes, setReengageMinutes] = useState<number | ''>('')
  // SA-268: same set-only contract as the handoff knobs above — `''` means
  // "not set" so an untouched field doesn't clobber the deployment's value.
  const [quoteDecisionReminderMinutes, setQuoteDecisionReminderMinutes] = useState<number | ''>('')
  // SA-1217: under the process engine the reminder is edited on the "<Noun> settings" page, so this
  // tab neither shows nor sends it (one editor per deployment). Strict: nothing moves before the
  // manifest loads.
  const reminderElsewhere = useFeatureStore((s) => s.isLoaded && s.features.has('process_engine'))
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { addToast } = useToast()

  useEffect(() => {
    apiClient
      .get('/api/commerce/business')
      .then((d) => {
        setName(d.business_name ?? '')
        setTimezone(d.timezone ?? null)
        setPhone(d.phone ?? '')
        setBankBeneficiary(d.bank_beneficiary_name ?? '')
        setBankName(d.bank_name ?? '')
        setBankClabe(d.bank_account_or_clabe ?? '')
        setBankInstructions(d.bank_instructions ?? '')
        setBankImage(d.bank_details_image ?? null)
        setSlaReminderMinutes(d.handoff_sla_reminder_minutes ?? '')
        setReengageMinutes(d.handoff_reengage_minutes ?? '')
        setQuoteDecisionReminderMinutes(d.quote_decision_reminder_minutes ?? '')
      })
      .catch(() => {
        setError('Failed to load business details')
        // A failed GET leaves every field at its empty default; Save must
        // stay disabled or it would full-replace the deployment's bank
        // details (and name/tz/phone) with nulls.
        setLoadFailed(true)
      })
      .finally(() => setIsLoading(false))
  }, [])

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      // SA-243: handoff_sla_reminder_minutes / handoff_reengage_minutes are
      // set-only on the PUT schema (Optional[int], ge=0) — an omitted key
      // leaves the deployment's current value alone, so a blanked input
      // must not send 0 (that would silently turn the reminder off).
      const body: Record<string, unknown> = {
        business_name: name || null,
        timezone: timezone || null,
        phone: phone || null,
        bank_beneficiary_name: bankBeneficiary || null,
        bank_name: bankName || null,
        bank_account_or_clabe: bankClabe || null,
        bank_instructions: bankInstructions || null,
        bank_details_image: bankImage || null,
      }
      if (slaReminderMinutes !== '') body.handoff_sla_reminder_minutes = slaReminderMinutes
      if (reengageMinutes !== '') body.handoff_reengage_minutes = reengageMinutes
      if (!reminderElsewhere && quoteDecisionReminderMinutes !== '') {
        body.quote_decision_reminder_minutes = quoteDecisionReminderMinutes
      }
      await apiClient.put('/api/commerce/business', body)
      addToast({ type: 'success', title: 'Business details saved' })
    } catch {
      setError('Save failed — check the timezone is a valid IANA name (e.g. America/Mexico_City)')
    } finally {
      setSaving(false)
    }
  }

  const handleImageUpload = async (file: File) => {
    setIsUploading(true)
    setUploadError(null)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const response = await apiClient.fetch('/api/media/upload', {
        method: 'POST',
        body: formData,
      })
      if (!response.ok) throw new Error(`Upload failed: ${response.statusText}`)
      const data = await response.json()
      setBankImage(data.media_ref ?? data.id ?? data.ref)
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setIsUploading(false)
    }
  }

  if (isLoading) return <div className="flex justify-center py-8"><Spinner /></div>

  return (
    <div className="space-y-6">
      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle>Business details</CardTitle>
          <CardDescription>
            Identity used across ordering, hours and courier handoff.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <Input
            label="Business name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <div>
            <label htmlFor="biz-timezone" className="mb-1.5 block text-sm font-medium text-foreground">
              Timezone
            </label>
            <TimezonePicker id="biz-timezone" value={timezone} onChange={setTimezone} />
            <p className="mt-1.5 text-sm text-muted-foreground">
              IANA timezone used for business-hours calculations.
            </p>
          </div>
          <Input
            label="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+52 55 1234 5678"
            hint="Contact phone; also the default courier sender phone."
          />
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <Button onClick={handleSave} isLoading={saving} disabled={loadFailed}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </CardContent>
      </Card>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle>Handoff follow-ups</CardTitle>
          <CardDescription>
            Nudges staff and re-engages the customer after a conversation is
            handed off to a human. 0 disables that nudge.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <Input
            label="Handoff reminder (minutes, 0 = off)"
            type="number"
            min={0}
            value={slaReminderMinutes}
            onChange={(e) =>
              setSlaReminderMinutes(e.target.value === '' ? '' : Number(e.target.value))
            }
          />
          <Input
            label="Customer re-engagement (minutes, 0 = off)"
            type="number"
            min={0}
            value={reengageMinutes}
            onChange={(e) =>
              setReengageMinutes(e.target.value === '' ? '' : Number(e.target.value))
            }
          />
          {!reminderElsewhere && (
            <Input
              label="Quote decision reminder (minutes, 0 = off)"
              type="number"
              min={0}
              value={quoteDecisionReminderMinutes}
              onChange={(e) =>
                setQuoteDecisionReminderMinutes(e.target.value === '' ? '' : Number(e.target.value))
              }
            />
          )}
        </CardContent>
      </Card>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle>Bank transfer details</CardTitle>
          <CardDescription>
            Deployment default sent to customers paying by SPEI transfer.
            Personas without their own CLABE inherit these; a persona CLABE
            overrides the whole set.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <Input
            label="Beneficiary name"
            value={bankBeneficiary}
            onChange={(e) => setBankBeneficiary(e.target.value)}
            placeholder="e.g. Dulces Hechizos S.A. de C.V."
          />
          <Input
            label="Bank"
            value={bankName}
            onChange={(e) => setBankName(e.target.value)}
            placeholder="e.g. Banorte"
          />
          <Input
            label="Account / CLABE"
            value={bankClabe}
            onChange={(e) => setBankClabe(e.target.value)}
            placeholder="18-digit CLABE or account number"
            hint="Customers transfer to this account. Leave blank to require per-persona configuration."
          />
          <div>
            <label htmlFor="biz-bank-instructions" className="mb-1.5 block text-sm font-medium text-foreground">
              Payment instructions
            </label>
            <Textarea
              id="biz-bank-instructions"
              value={bankInstructions}
              onChange={(e) => setBankInstructions(e.target.value)}
              placeholder="e.g. Transfer the exact amount and send a screenshot of the receipt."
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              Bank details image (optional)
            </label>
            {bankImage ? (
              <div className="flex items-center gap-3 text-sm">
                <span className="truncate font-mono text-muted-foreground">{bankImage}</span>
                <button
                  type="button"
                  className="text-destructive underline hover:no-underline shrink-0"
                  onClick={() => {
                    setBankImage(null)
                    if (fileInputRef.current) fileInputRef.current.value = ''
                  }}
                >
                  Remove
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="text-sm text-primary underline hover:no-underline disabled:opacity-50"
              >
                {isUploading ? 'Uploading…' : 'Upload bank details card / QR code'}
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) void handleImageUpload(file)
                e.target.value = ''
              }}
              data-testid="bank-image-file-input"
            />
            {uploadError && <p className="text-xs text-destructive">{uploadError}</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
